// Cargo para corregir un bug en typecheck
import type { CheckedState } from "@radix-ui/react-checkbox";

import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import {
  ChevronUp,
  Menu,
  X,
  ChevronLeft,
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
const labImage1 = "/assets/f9564ab801a1786dc9a7f34bcbbceeaa941ff959.png";
// Importar los nuevos componentes
import { CoursesComparisonTable } from "./courses/CoursesComparisonTable";
import { InstructorBio } from "./instructor/InstructorBio";
import { ScheduleAndFAQ } from "./schedule/ScheduleAndFAQ";
import { ContactForm } from "./forms/ContactForm";

// Importar los datos
import { comparisonTableData, scheduleData } from "../data/breeders";

interface BreedersPageProps {
  onNavigateBack: () => void;
  onNavigateToCursoOnline: () => void;
  onNavigateToCursoAvanzado: () => void;
  onNavigateToPresencial: () => void;
  onNavigateToCheckout: (courseId: string) => void;
}

export function BreedersPage({ 
  onNavigateBack, 
  onNavigateToCursoOnline, 
  onNavigateToCursoAvanzado, 
  onNavigateToPresencial, 
  onNavigateToCheckout 
}: BreedersPageProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      text: t("testimonials.testimonial1"),
      image: "/assets/entretenimiento.png",
      instagram: "@micultivomientretenimiento3.0"
    },
    {
      text: t("testimonials.testimonial2"),
      image: "/assets/hortilive.png",
      instagram: "@hortlilive"
    },
    {
      text: t("testimonials.testimonial3"),
      image: "/assets/universal.png",
      instagram: "@universalgrow"
    }
  ];

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const backgroundY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "100%"],
  );
  const textY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "200%"],
  );

  // Control scroll to top button and navbar visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 500);
      setShowNavbar(scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-rotate testimonials every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(
        (prev) => (prev + 1) % testimonials.length,
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setMobileMenuOpen(false);
    }
  };

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navItems = [
    { id: "inicio", label: t("breeders.navigation.home") },
    { id: "cursos", label: t("breeders.navigation.courses") },
    { id: "comparativa", label: t("breeders.navigation.comparison") },
    { id: "profesor", label: t("breeders.navigation.professor") },
    { id: "calendario", label: t("breeders.navigation.calendar") },
    { id: "contacto", label: t("breeders.navigation.contact") },
  ];

  const handleContactSubmit = async (data: {
    name: string;
    email: string;
    phone?: string;
    experience?: string;
    acceptedTerms: boolean;
  }) => {
    // Aquí iría la lógica de envío del formulario
    console.log("Form data:", data);
    // Por ahora solo mostramos un alert
    alert("Formulario enviado correctamente");
  };

  const handleEnroll = (courseKey: string) => {
    switch (courseKey) {
      case "onlineInitiation":
        onNavigateToCursoOnline();
        break;
      case "onlineAdvanced":
        onNavigateToCursoAvanzado();
        break;
      case "presencial":
        onNavigateToPresencial();
        break;
      default:
        onNavigateToCheckout(courseKey);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Navigation Bar */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: showNavbar ? 0 : -100,
          opacity: showNavbar ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-orange-500/30"
        style={{ pointerEvents: showNavbar ? "auto" : "none" }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateBack}
                className="text-white hover:text-orange-500 mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t("breeders.navigation.back")}
              </Button>
              <div
                className="flex items-center cursor-pointer"
                onClick={() => scrollToSection("inicio")}
              >
                <img
                  src={rebelLogo}
                  alt="Rebel Genomics"
                  className="w-8 h-8 mr-2"
                />
                <span className="text-white text-lg">
                  RebelGenomics
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-white hover:text-orange-500 transition-colors duration-200 text-sm uppercase tracking-wide"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden bg-black/95 border-t border-orange-500/30"
            >
              <div className="py-4 space-y-4">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-2 text-white hover:text-orange-500 transition-colors duration-200 text-sm uppercase tracking-wide"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: showScrollTop ? 1 : 0,
          scale: showScrollTop ? 1 : 0.8,
        }}
        transition={{ duration: 0.3 }}
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 z-50 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg transition-colors duration-200"
        style={{
          pointerEvents: showScrollTop ? "auto" : "none",
        }}
      >
        <ChevronUp size={24} />
      </motion.button>

      {/* Hero Section - Bloque 1 */}
      <section
        id="inicio"
        className="relative h-screen overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 flex items-center justify-center pt-16"
      >
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1659283502485-3cb8db512fcd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0aXNzdWUHMjUlMjBjdWx0dXJlJTIwbGFib3JhdG9yeSUyMGphcnMlMjBwbGFudHN8ZW58MXx8fHwxNzU3ODcxODM2fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Tissue Culture Laboratory Jars"
            className="w-full h-full object-cover opacity-30"
          />
        </motion.div>

        <motion.div
          style={{ y: textY }}
          className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto"
        >
          <motion.img
            src={rebelLogo}
            alt="Rebel Genomics"
            className="w-32 h-32 mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />

          <motion.h1
            className="text-5xl md:text-8xl mb-8 tracking-tight leading-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {t("breeders.hero.title1")}
            <br />
            <span className="text-black">{t("breeders.hero.title2")}</span>
            <br />
            <span className="text-3xl md:text-5xl text-yellow-200">
              {t("breeders.hero.title3")}
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            {t("breeders.hero.description")}
          </motion.p>

          {/* Testimonials Carousel */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="relative max-w-3xl mx-auto"
          >
            <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/30 min-h-[200px] flex items-center">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="w-full text-center"
              >
                {/* Testimonial Text */}
                <blockquote className="text-white text-lg md:text-xl mb-8 italic leading-relaxed">
                  "{testimonials[currentTestimonial].text}"
                </blockquote>

                {/* Profile and Instagram - Side by side */}
                <div className="flex items-center justify-center gap-4">
                  <ImageWithFallback
                    src={testimonials[currentTestimonial].image}
                    alt={t("testimonials.altText")}
                    className="w-16 h-16 rounded-full object-cover border-3 border-orange-500"
                  />
                  <p className="text-orange-300 text-lg">{testimonials[currentTestimonial].instagram}</p>
                </div>
              </motion.div>
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial
                      ? "bg-orange-500 scale-125"
                      : "bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Cursos Disponibles - Bloque 2 */}
      <section
        id="cursos"
        className="min-h-screen bg-white text-black flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              {t("breeders.courses.title1")}
              <br />
              <span className="text-black">
                {t("breeders.courses.title2")}
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Curso Online Iniciación */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-orange-500/20 p-8 rounded-3xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1616078206521-5d7e6dc51545?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwcGxhbnRzJTIwaW4lMjB2aXRybyUyMGN1bHRpdmF0aW9ufGVufDF8fHx8MTc1Nzg3MTg0Mnww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Medical Plants In Vitro"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl mb-4 text-orange-500 uppercase font-bold text-center">
                  {t("breeders.courses.onlineInitiation.title")}
                </h3>
                <h4 className="text-lg mb-4 text-gray-800 font-semibold">
                  {t("breeders.courses.onlineInitiation.subtitle")}
                </h4>
                <div className="space-y-3 mb-8">
                  {(t("breeders.courses.onlineInitiation.features", { returnObjects: true }) as string[]).map((feature, index) => (
                    <div key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                  </div>
                  ))}
                </div>
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3 rounded-full"
                  onClick={onNavigateToCursoOnline}
                >
                  {t("breeders.courses.onlineInitiation.button")}
                </Button>
              </Card>
            </motion.div>

            {/* Curso Online Avanzado */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-orange-500/20 p-8 rounded-3xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1589856188845-b7601678c0c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjYW5uYWJpcyUyMGJyZWVkZXIlMjBnZW5ldGljcyUyMGxhYm9yYXRvcnl8ZW58MXx8fHwxNzU3ODcxODM5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Cannabis Genetics Laboratory"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl mb-4 text-orange-500 uppercase font-bold text-center">
                  {t("breeders.courses.onlineAdvanced.title")}
                </h3>
                <h4 className="text-lg mb-4 text-gray-800 font-semibold">
                  {t("breeders.courses.onlineAdvanced.subtitle")}
                </h4>
                <div className="space-y-3 mb-8">
                  {(t("breeders.courses.onlineAdvanced.features", { returnObjects: true }) as string[]).map((feature, index) => (
                    <div key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                  </div>
                  ))}
                </div>
                <Button
                  size="lg"
                  onClick={onNavigateToCursoAvanzado}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3 rounded-full"
                >
                  {t("breeders.courses.onlineAdvanced.button")}
                </Button>
              </Card>
            </motion.div>

            {/* Masterclass Presencial */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-orange-500/20 p-8 rounded-3xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <img
                  src={labImage1}
                  alt="Masterclass Presencial RebelGenomics"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl mb-4 text-orange-500 uppercase font-bold text-center">
                  {t("breeders.courses.presencial.title")}
                </h3>
                <h4 className="text-lg mb-4 text-gray-800 font-semibold">
                  {t("breeders.courses.presencial.subtitle")}
                </h4>
                <div className="space-y-3 mb-8">
                  {(t("breeders.courses.presencial.features", { returnObjects: true }) as string[]).map((feature, index) => (
                    <div key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                  </div>
                  ))}
                </div>
                <Button
                  size="lg"
                  onClick={onNavigateToPresencial}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3 rounded-full"
                >
                  {t("breeders.courses.presencial.button")}
                </Button>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabla Comparativa - Bloque 3 */}
      <section
        id="comparativa"
        className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              {t("breeders.comparison.title1")}
              <br />
              <span className="text-black">
                {t("breeders.comparison.title2")}
              </span>
            </h2>
          </motion.div>

          <CoursesComparisonTable
            columns={comparisonTableData.columns}
            rows={comparisonTableData.rows}
            notes={comparisonTableData.notes}
            onEnroll={handleEnroll}
          />
        </div>
      </section>

      {/* Sobre el Profesor - Bloque 4 */}
      <InstructorBio />

      {/* Calendario y FAQs - Bloque 5 */}
      <ScheduleAndFAQ
        dates={scheduleData.dates}
        faq={scheduleData.faq}
        defaultOpenFaqIndex={0}
        onCta={handleEnroll}
      />

      {/* Contacto y Lead Magnet - Bloque 6 */}
      <ContactForm
        onSubmit={handleContactSubmit}
        requireAcceptedTerms
        privacyUrl="/privacidad"
      />
    </div>
  );
}
